import React, { useEffect, useState } from 'react';
import { ImageIcon, ZoomIn } from 'lucide-react';
import { productService } from '@services/productService';

/**
 * Thumb da Foto do Projeto de um produto personalizado, com lightbox.
 * O container do blob é PRIVADO: o binário vem do proxy
 * GET /products/:id/project-photo/file como object URL (revogado no cleanup).
 * Renderize apenas quando `has_project_photo` do item/produto for true.
 */
export const ProductProjectPhotoThumb: React.FC<{
  productId: number;
  /** compact = ícone pequeno inline; normal = thumb 64px */
  compact?: boolean;
  className?: string;
}> = ({ productId, compact = false, className = '' }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    productService
      .getProjectPhotoFile(productId)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => {
        // Sem foto ou erro de rede: não renderiza nada.
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [productId]);

  if (!url) return null;

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title="Ver foto do projeto"
          className={`inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 ${className}`}
        >
          <ImageIcon size={12} /> Foto do projeto
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title="Ampliar foto do projeto"
          className={`relative group ${className}`}
        >
          <img
            src={url}
            alt="Foto do projeto"
            className="w-16 h-16 object-cover rounded-lg border border-slate-200"
          />
          <span className="absolute inset-0 hidden group-hover:flex items-center justify-center bg-black/30 rounded-lg text-white">
            <ZoomIn size={16} />
          </span>
        </button>
      )}
      {expanded && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6"
          onClick={() => setExpanded(false)}
        >
          <img src={url} alt="Foto do projeto" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </>
  );
};
